import { type ComponentProps, type Dispatch, type ReactNode, type SetStateAction, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Checkbox, ClipLoader, color, Icon, Table } from '@wf/components';

import EmptyTableContent from '../EmptyTableContent';

import useCheckboxes from '../../hooks/useCheckboxes';

import styles from './TableGrid.module.scss';

export interface ITableColumn<T> {
  key: string;
  name: string;
  flex?: number | string;
  sortable?: boolean;
  colRenderer?: (value: any, index: number, rowData: T) => ReactNode;
  nameRenderer?: () => ReactNode;
  className?: string;
  align?: ComponentProps<typeof Table.Cell>['align'];
}

interface ITableGrid<T> {
  columns: ITableColumn<T>[];
  rows: T[];
  maxRows?: number;
  onRowClick?: (rowData: T) => void;
  handleSort?: (sortKey: string, isAsc: boolean) => void;
  isSortedBy?: string;
  isAsc?: boolean;
  isLoading?: boolean;
  noResultTitle?: string;
  noResultDescription?: string;
  emptyTableContent?: ReactNode;
  rowKey?: string;
  withScrolling?: boolean;
  checkedItemIds?: Set<string | number>;
  setCheckedItemIds?: Dispatch<SetStateAction<Set<string | number>>>;
  showIndex?: boolean;
  errors?: Record<number, any>;
}

function TableGrid<TRow extends Record<string, any>>({
  columns,
  rows,
  maxRows,
  onRowClick,
  handleSort,
  isAsc,
  isSortedBy,
  checkedItemIds,
  setCheckedItemIds,
  emptyTableContent,
  isLoading = false,
  noResultTitle = '',
  noResultDescription = '',
  rowKey = 'id',
  withScrolling = false,
  showIndex = false,
  errors,
}: ITableGrid<TRow>) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  const hasCheckboxSelection = !!checkedItemIds;

  const { handleCheck, handleCheckAll, allItemsAreChecked, someItemsAreChecked } = useCheckboxes({
    data: rows,
    checkedItemIds: checkedItemIds ?? new Set(),
    setCheckedItemIds: setCheckedItemIds ?? (() => null),
    key: rowKey,
  });

  // TODO this is temp solution, will be removed after we have
  //  design compliant solution of showing more than 5 items in table
  useEffect(() => {
    if (withScrolling) {
      ref?.current?.scrollTo({ top: 0 });
    }
  }, [rows, withScrolling]);

  return (
    <Table.Grid className={cn(styles.tableGrid, { [styles.withScrolling as string]: withScrolling })}>
      <Table.Header className={styles.tableHeader}>
        <Table.Row>
          {hasCheckboxSelection && (
            <Table.HeaderCell className={styles.checkboxCell}>
              {!!rows.length && (
                <Checkbox
                  intermediate={someItemsAreChecked && !allItemsAreChecked}
                  checked={someItemsAreChecked}
                  onChange={handleCheckAll}
                />
              )}
            </Table.HeaderCell>
          )}
          {showIndex && (
            <Table.HeaderCell className={styles.numberCell}>
              <span>#</span>
            </Table.HeaderCell>
          )}
          {columns.map((item) => {
            const { flex, name, nameRenderer, sortable, key, className, align } = item;

            return (
              <Table.HeaderCell
                onClick={() => {
                  sortable && handleSort?.(key, isSortedBy === key ? !isAsc : false);
                }}
                className={cn(styles.tableCell, className)}
                style={{ flex }}
                align={align}
                key={name}
              >
                <div
                  className={cn(styles.headerCell, {
                    [styles.sortable as string]: sortable,
                    [styles.alignRight as string]: align === 'right',
                    [styles.alignCenter as string]: align === 'center',
                  })}
                >
                  {nameRenderer ? nameRenderer() : <span>{t(name)}</span>}
                  {isSortedBy === key && (
                    <Icon
                      name={isAsc ? 'ic_sorting_up' : 'ic_sorting_down'}
                      color={color('typography', 'light')}
                      size={20}
                    />
                  )}
                </div>
              </Table.HeaderCell>
            );
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body className={styles.tableBody} maxRows={maxRows} ref={ref}>
        {rows.map((row, index) => (
          <Table.Row
            className={cn({ [styles.clickableRow as string]: !!onRowClick })}
            onClick={() => onRowClick?.(row)}
            hasError={!!errors?.[index]}
            key={row[rowKey]}
          >
            {hasCheckboxSelection && (
              <Table.Cell className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  onChange={(checked) => handleCheck(checked, row[rowKey] as string)}
                  checked={checkedItemIds?.has(row[rowKey] as string)}
                />
              </Table.Cell>
            )}
            {showIndex && (
              <Table.Cell className={styles.numberCell}>
                {row.index != null ? (row.index as number) + 1 : index + 1}
              </Table.Cell>
            )}
            {columns.map(({ name, flex, key, colRenderer, className, align }) => (
              <Table.Cell key={name} style={{ flex }} className={cn(styles.tableCell, className)} align={align}>
                {colRenderer ? colRenderer(row[key], index, row) : row[key]}
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
        {!isLoading &&
          !rows.length &&
          (emptyTableContent ?? <EmptyTableContent title={noResultTitle} text={noResultDescription} />)}
      </Table.Body>
      {/*TODO: Skeleton loading*/}
      {isLoading && (
        <div className={styles.loader}>
          <ClipLoader />
        </div>
      )}
    </Table.Grid>
  );
}

export default TableGrid;
